/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuthStore, DEMO_USER } from "@/stores";
import { setupKeyboardShortcuts } from "@/stores";
import { Toaster } from "@/components/ui/toaster";
import { HookToaster } from "@/components/ui/hook-toaster";
import { Toaster as SonnerToaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "@/components/layout";
import { ProtectedRoute, RequireIntegration } from "@/components/auth";
import { ThemeProvider } from "@/components/ThemeProvider";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { Button } from "@/components/ui/button";
import { useIsAuthenticated } from "@azure/msal-react";

// MVP Context Providers
import { FilterProvider } from "@/context/FilterContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { PermissionProvider } from "@/context/PermissionContext";
import { RequirePermission } from "@/components/auth/RequirePermission";

// Lazy load pages for code splitting
const Login = lazy(() => import("@/pages/Login"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Submissions = lazy(() => import("@/pages/Submissions"));
const SubmissionDetail = lazy(() => import("@/pages/SubmissionDetail"));
const NewSubmission = lazy(() => import("@/pages/NewSubmission"));
const Incidents = lazy(() => import("@/pages/Incidents"));
const IncidentAnalytics = lazy(() => import("@/pages/Incidents/Analytics"));
const IncidentDetail = lazy(() => import("@/pages/Incidents/IncidentDetail"));
const Alerts = lazy(() => import("@/pages/Alerts"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const Reports = lazy(() => import("@/pages/Reports"));
const Profile = lazy(() => import("@/pages/Profile"));

// Admin pages
const Admin = lazy(() => import("@/pages/admin"));
const AdminSystemSettings = lazy(() => import("@/pages/admin/system-settings"));
const AdminMeasures = lazy(() => import("@/pages/admin/Measures"));
const AdminReconciliation = lazy(() => import("@/pages/admin/reconciliation"));
const AdminThresholds = lazy(() => import("@/pages/admin/thresholds"));
const AdminStateConfiguration = lazy(() => import("@/pages/admin/StateConfiguration"));
const AdminValidationConfig = lazy(() => import("@/pages/admin/ValidationConfig"));
const AdminIdentityProviders = lazy(() => import("@/pages/admin/IdentityProviders"));
const AdminIntegrations = lazy(() => import("@/pages/admin/integrations"));
const AdminSecurity = lazy(() => import("@/pages/admin/security"));
const AdminChangeRequests = lazy(() => import("@/pages/admin/change-requests"));
const AdminAudit = lazy(() => import("@/pages/admin/audit"));
const AdminUsers = lazy(() => import("@/pages/admin/users"));

// Help page
const Help = lazy(() => import("@/pages/Help"));

// Page loading fallback
function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );
}

// Main App component
export default function App() {
  const {
    isAuthenticated: torqeIsAuthenticated,
    isDemoMode,
    enableDemoMode,
    refreshUserFromMsal,
  } = useAuthStore();

  // Check if MSAL has an authenticated user
  const msalIsAuthenticated = useIsAuthenticated();

  // Track if we're initializing auth
  const [isInitializing, setIsInitializing] = useState(true);

  // Setup keyboard shortcuts
  useEffect(() => {
    return setupKeyboardShortcuts();
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      const isDemoModeEnv = import.meta.env.VITE_DEMO_MODE === "true";

      try {
        // Priority 1: If MSAL is authenticated, sync with Zustand store
        if (msalIsAuthenticated && !torqeIsAuthenticated) {
          await refreshUserFromMsal();

          // Check if there's a return URL from token acquisition
          const returnUrl = sessionStorage.getItem("torqe-auth-return-url");
          if (returnUrl) {
            sessionStorage.removeItem("torqe-auth-return-url");
            window.location.href = returnUrl;
            return; // Don't set isInitializing false, page will reload
          }

          // Mark initialization complete AFTER sync
          setIsInitializing(false);
          return;
        }

        // Priority 2: If demo mode is enabled and nothing is authenticated, use demo user
        if (isDemoModeEnv && !torqeIsAuthenticated && !msalIsAuthenticated) {
          enableDemoMode(DEMO_USER);
          setIsInitializing(false);
          return;
        }

        // Otherwise, no action needed - user will need to login
        setIsInitializing(false);
      } catch {
        setIsInitializing(false);
      }
    };

    void initializeAuth();
  }, [msalIsAuthenticated, torqeIsAuthenticated, isDemoMode, refreshUserFromMsal, enableDemoMode]);

  // Show loading screen while initializing auth
  if (isInitializing) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin text-orange-500" />
          <p className="text-muted-foreground">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <FilterProvider>
      <BrandingProvider>
        <ThemeProvider>
          <BrowserRouter>
            <TooltipProvider>
              <Routes>
                {/* Public routes */}
                <Route
                  path="/login"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <Login />
                    </Suspense>
                  }
                />

                {/* Protected routes - all require authentication */}
                <Route element={<ProtectedRoute />}>
                  <Route
                    element={
                      <PermissionProvider>
                        <Layout />
                      </PermissionProvider>
                    }
                  >
                    {/* Dashboard */}
                    <Route
                      index
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Dashboard />
                        </Suspense>
                      }
                    />
                    <Route path="/dashboard" element={<Navigate to="/" replace />} />

                    {/* Submissions */}
                    <Route
                      path="/submissions"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Submissions />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/submissions/new"
                      element={
                        <RequirePermission code="submissions:create">
                          <Suspense fallback={<PageLoader />}>
                            <NewSubmission />
                          </Suspense>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/submissions/:id"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <SubmissionDetail />
                        </Suspense>
                      }
                    />

                    {/* Incidents — visible only when an external integration is configured
                        AND the user holds incidents:view. */}
                    <Route
                      path="/incidents"
                      element={
                        <RequirePermission code="incidents:view">
                          <RequireIntegration>
                            <ErrorBoundary>
                              <Suspense fallback={<PageLoader />}>
                                <Incidents />
                              </Suspense>
                            </ErrorBoundary>
                          </RequireIntegration>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/incidents/analytics"
                      element={
                        <RequirePermission code="incidents:view">
                          <RequireIntegration>
                            <ErrorBoundary>
                              <Suspense fallback={<PageLoader />}>
                                <IncidentAnalytics />
                              </Suspense>
                            </ErrorBoundary>
                          </RequireIntegration>
                        </RequirePermission>
                      }
                    />
                    <Route
                      path="/incidents/:id"
                      element={
                        <RequirePermission code="incidents:view">
                          <RequireIntegration>
                            <ErrorBoundary>
                              <Suspense fallback={<PageLoader />}>
                                <IncidentDetail />
                              </Suspense>
                            </ErrorBoundary>
                          </RequireIntegration>
                        </RequirePermission>
                      }
                    />

                    {/* Alerts */}
                    <Route
                      path="/alerts"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <Alerts />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />

                    {/* Analytics */}
                    <Route
                      path="/analytics"
                      element={
                        <RequirePermission code="analytics:view">
                          <ErrorBoundary>
                            <Suspense fallback={<PageLoader />}>
                              <Analytics />
                            </Suspense>
                          </ErrorBoundary>
                        </RequirePermission>
                      }
                    />

                    {/* Reports: gated by the reports:view permission (state_admin, system_admin,
                        analyst, submitter — developer and viewer/"Executive Viewer" excluded). The
                        backend enforces the same permission plus per-state access on every request. */}
                    <Route
                      path="/reports"
                      element={
                        <RequirePermission code="reports:view">
                          <ErrorBoundary>
                            <Suspense fallback={<PageLoader />}>
                              <Reports />
                            </Suspense>
                          </ErrorBoundary>
                        </RequirePermission>
                      }
                    />

                    {/*
                      Activity: top-level, unguarded. GET /audit/me is
                      available to any authenticated user; admins with
                      ``admin:view_audit`` also see the scope toggle
                      inside the page to flip to org-wide via GET /audit/.
                      DELIBERATELY NOT under the /admin permission gate
                      so non-admins can actually reach it.
                      ``ErrorBoundary`` wraps ``Suspense`` so chunk-load
                      errors are caught here; the reverse order lets
                      Suspense propagate the error past the boundary.
                    */}
                    <Route
                      path="/activity"
                      element={
                        <ErrorBoundary>
                          <Suspense fallback={<PageLoader />}>
                            <AdminAudit />
                          </Suspense>
                        </ErrorBoundary>
                      }
                    />
                    {/*
                      Legacy bookmark redirect. Must live outside the
                      admin permission gate: a user with an old
                      ``/admin/audit`` bookmark who has since lost
                      (or never held) admin perms should still be
                      redirected, not hit NoAccess before Navigate
                      fires.
                    */}
                    <Route path="/admin/audit" element={<Navigate to="/activity" replace />} />

                    {/* Profile */}
                    <Route
                      path="/profile"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ErrorBoundary>
                            <Profile />
                          </ErrorBoundary>
                        </Suspense>
                      }
                    />

                    {/* Help */}
                    <Route
                      path="/help"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <Help />
                        </Suspense>
                      }
                    />

                    {/* Admin Routes - permission-gated, ErrorBoundary per feature */}
                    <Route
                      element={
                        <RequirePermission
                          codes={[
                            "admin:view_reference",
                            "admin:manage_users",
                            "admin:edit_config",
                            "admin:manage_integrations",
                            "admin:view_audit",
                          ]}
                        />
                      }
                    >
                      <Route
                        path="/admin"
                        element={
                          <Suspense fallback={<PageLoader />}>
                            <ErrorBoundary>
                              <Admin />
                            </ErrorBoundary>
                          </Suspense>
                        }
                      />
                      <Route
                        path="/admin/system-settings"
                        element={
                          <RequirePermission code="admin:manage_users">
                            <Suspense fallback={<PageLoader />}>
                              <ErrorBoundary>
                                <AdminSystemSettings />
                              </ErrorBoundary>
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="/admin/measures"
                        element={
                          <RequirePermission code="admin:view_reference">
                            <Suspense fallback={<PageLoader />}>
                              <ErrorBoundary>
                                <AdminMeasures />
                              </ErrorBoundary>
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="/admin/reconciliation"
                        element={
                          <RequirePermission code="admin:manage_reconciliation">
                            <Suspense fallback={<PageLoader />}>
                              <ErrorBoundary>
                                <AdminReconciliation />
                              </ErrorBoundary>
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="/admin/thresholds"
                        element={
                          <RequirePermission code="admin:view_reference">
                            <Suspense fallback={<PageLoader />}>
                              <ErrorBoundary>
                                <AdminThresholds />
                              </ErrorBoundary>
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="/admin/state-configuration"
                        element={
                          <RequirePermission code="admin:view_reference">
                            <Suspense fallback={<PageLoader />}>
                              <ErrorBoundary>
                                <AdminStateConfiguration />
                              </ErrorBoundary>
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="/admin/validation-config"
                        element={
                          <RequirePermission role="state_admin">
                            <Suspense fallback={<PageLoader />}>
                              <ErrorBoundary>
                                <AdminValidationConfig />
                              </ErrorBoundary>
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="/admin/identity-providers"
                        element={
                          <RequirePermission code="admin:manage_users">
                            <Suspense fallback={<PageLoader />}>
                              <ErrorBoundary>
                                <AdminIdentityProviders />
                              </ErrorBoundary>
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="/admin/integrations"
                        element={
                          <RequirePermission code="admin:manage_integrations">
                            <ErrorBoundary
                              fallback={
                                <div className="flex min-h-screen items-center justify-center">
                                  <div className="text-center">
                                    <h2 className="text-2xl font-bold text-red-600">
                                      Something went wrong
                                    </h2>
                                    <p className="mt-2 text-gray-600">
                                      Failed to load integrations page
                                    </p>
                                    <Button
                                      onClick={() => {
                                        window.location.reload();
                                      }}
                                      className="mt-4"
                                    >
                                      Reload Page
                                    </Button>
                                  </div>
                                </div>
                              }
                            >
                              <Suspense fallback={<PageLoader />}>
                                <AdminIntegrations />
                              </Suspense>
                            </ErrorBoundary>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="/admin/security"
                        element={
                          <RequirePermission code="admin:manage_users">
                            <Suspense fallback={<PageLoader />}>
                              <ErrorBoundary>
                                <AdminSecurity />
                              </ErrorBoundary>
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="/admin/notifications"
                        element={<Navigate to="/profile" replace />}
                      />
                      <Route
                        path="/admin/change-requests"
                        element={
                          <RequirePermission code="change_requests:view">
                            <Suspense fallback={<PageLoader />}>
                              <ErrorBoundary>
                                <AdminChangeRequests />
                              </ErrorBoundary>
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                      <Route
                        path="/admin/users"
                        element={
                          <RequirePermission code="admin:manage_users">
                            <Suspense fallback={<PageLoader />}>
                              <ErrorBoundary>
                                <AdminUsers />
                              </ErrorBoundary>
                            </Suspense>
                          </RequirePermission>
                        }
                      />
                    </Route>
                    {/* end admin permission gate */}

                    {/* 404 */}
                    <Route
                      path="*"
                      element={
                        <div className="flex h-96 flex-col items-center justify-center">
                          <h1 className="mb-2 text-4xl font-bold">404</h1>
                          <p className="text-muted-foreground">Page not found</p>
                        </div>
                      }
                    />
                  </Route>
                </Route>
              </Routes>
              <Toaster />
              <HookToaster />
              <SonnerToaster
                position="top-right"
                theme="system"
                containerAriaLabel="Sonner toast messages"
                toastOptions={{
                  classNames: {
                    toast: "bg-background text-foreground border-border",
                    success:
                      "bg-green-50 dark:bg-green-950 text-green-900 dark:text-green-100 border-green-200 dark:border-green-800",
                    error:
                      "bg-red-50 dark:bg-red-950 text-red-900 dark:text-red-100 border-red-200 dark:border-red-800",
                    info: "bg-blue-50 dark:bg-blue-950 text-blue-900 dark:text-blue-100 border-blue-200 dark:border-blue-800",
                    warning:
                      "bg-yellow-50 dark:bg-yellow-950 text-yellow-900 dark:text-yellow-100 border-yellow-200 dark:border-yellow-800",
                    title: "text-foreground font-semibold",
                    description: "text-muted-foreground",
                  },
                }}
              />
            </TooltipProvider>
          </BrowserRouter>
        </ThemeProvider>
      </BrandingProvider>
    </FilterProvider>
  );
}
